import React, { useMemo, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Tooltip,
    Stack,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTheme } from '@mui/material/styles';
import { Campaign, DateRequest, CampaignDates } from '../../../../types/Campaign';
import { AutoAwesome } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import AIService from '../../../../services/AIService';

// Define interfaces for props
interface DatesProps {
    campaign: Campaign;
    onSave?: (updatedCampaign: Partial<Campaign>) => Promise<Campaign>;
}

// Timeline event for visualization
interface TimelineEvent {
    id: string;
    name: string;
    tooltip: string;
    date: Date | null;
    color?: string;
}

// Inline editable date field
interface EditableDateFieldProps {
    label: string;
    value: string | null | undefined;
    tooltip: string;
    fieldKey: keyof Campaign;
    onDateChange: (field: keyof Campaign, newValue: Date | null) => void;
}

const EditableDateField: React.FC<EditableDateFieldProps> = ({ label, value, tooltip, fieldKey, onDateChange }) => {
    const isPast = (() => {
        if (!value) return false;
        const date = new Date(value);
        if (isNaN(date.getTime())) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
    })();

    return (
        <Grid item xs={12} sm={6} md={4}>
            <Tooltip title={tooltip}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%', borderColor: isPast ? 'error.main' : 'divider', backgroundColor: isPast ? 'rgba(255, 0, 0, 0.04)' : 'inherit' }}>
                    <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
                    <DatePicker
                        value={value ? new Date(value) : null}
                        onChange={(newVal) => onDateChange(fieldKey, newVal)}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                variant: 'standard',
                                error: isPast,
                                helperText: isPast ? 'Date is in the past' : undefined,
                                sx: {
                                    '& .MuiInputBase-input': {
                                        color: isPast ? 'error.main' : 'text.primary'
                                    }
                                }
                            }
                        }}
                    />
                </Paper>
            </Tooltip>
        </Grid>
    );
};

const Dates: React.FC<DatesProps> = ({ campaign, onSave }) => {
    const { organization_id: organizationId, campaign_id: campaignId } = useParams<{ organization_id: string; campaign_id: string }>();

    // Local draft copy so inline edits are immediately reflected
    const [draftCampaign, setDraftCampaign] = useState<Campaign>(campaign);

    React.useEffect(() => {
        setDraftCampaign(campaign);
    }, [campaign]);

    const [loading, setLoading] = useState<boolean>(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
        open: false,
        message: '',
        severity: 'info'
    });

    // Modal state
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [kickoffDate, setKickoffDate] = useState<Date | null>(campaign.kickoff ? new Date(campaign.kickoff) : null);
    const [closingDate, setClosingDate] = useState<Date | null>(campaign.closing ? new Date(campaign.closing) : null);
    const [dateError, setDateError] = useState<string>('');

    const theme = useTheme();

    const safeFormatDate = (dateStr: string | null | undefined): Date | null => {
        if (!dateStr) return null;
        const date = new Date(dateStr);
        return isNaN(date.getTime()) ? null : date;
    };

    const timelineEvents: TimelineEvent[] = useMemo(() => [
        { id: 'event-1', name: 'Apps Open', tooltip: 'Start date for accepting applications', date: safeFormatDate(campaign.applications_open), color: theme.palette.primary.main },
        { id: 'event-2', name: 'Apps Close', tooltip: 'Deadline for submitting applications', date: safeFormatDate(campaign.applications_close), color: theme.palette.primary.main },
        { id: 'event-3', name: 'Matches Sent', tooltip: 'Date when matches are notified', date: safeFormatDate(campaign.match_notification_date), color: theme.palette.warning.main },
        { id: 'event-4', name: 'Matches Final', tooltip: 'Deadline for participants to acknowledge matches', date: safeFormatDate(campaign.matches_finalized), color: theme.palette.warning.main },
        { id: 'event-5', name: 'Kickoff', tooltip: 'Official start date of the mentorship program', date: safeFormatDate(campaign.kickoff), color: theme.palette.success.main },
        { id: 'event-6', name: 'Closing', tooltip: 'Final date of the mentorship program', date: safeFormatDate(campaign.closing), color: theme.palette.success.main },
        { id: 'event-7', name: 'Feedback Due', tooltip: 'Deadline for submitting feedback', date: safeFormatDate(campaign.feedback_deadline), color: theme.palette.success.main },
    ], [campaign, theme]);

    const getDaysBetween = (startEvent: TimelineEvent, endEvent: TimelineEvent): number | null => {
        if (!startEvent.date || !endEvent.date) return null;
        return Math.round((endEvent.date.getTime() - startEvent.date.getTime()) / (1000 * 60 * 60 * 24));
    };

    const renderTimeline = () => (
        <Paper sx={{ p: 3, mb: 3, overflowX: 'auto', boxShadow: 'none', border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3, fontWeight: 600, color: theme.palette.text.primary }}>
                Campaign Timeline
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '800px', py: 2, position: 'relative' }}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    right: 0,
                    height: '2px',
                    backgroundColor: theme.palette.divider,
                    zIndex: 1
                }} />

                {timelineEvents.map((event, index) => {
                    const daysToNext = index < timelineEvents.length - 1 ? getDaysBetween(event, timelineEvents[index + 1]) : null;

                    return (
                        <Box key={event.id} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            position: 'relative',
                            zIndex: 2,
                            '&:not(:last-child)': {
                                flex: daysToNext ? '1 1 auto' : '0 0 auto',
                            },
                            '&:last-child': {
                                flex: '0 0 auto',
                            }
                        }}>
                            <Tooltip
                                title={`${event.name}: ${event.date ? event.date.toLocaleDateString() : 'Not set'}. ${event.tooltip}`}
                                arrow
                            >
                                <Stack
                                    alignItems="center"
                                    sx={{
                                        textAlign: 'center',
                                        minWidth: 100,
                                        p: 1,
                                        backgroundColor: 'background.paper',
                                        borderRadius: 1,
                                        border: `1px solid ${event.date ? event.color : theme.palette.grey[300]}`,
                                        '&:hover': {
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        }
                                    }}
                                >
                                    <Box sx={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: '50%',
                                        bgcolor: event.date ? event.color : theme.palette.grey[400],
                                        border: `2px solid ${theme.palette.background.paper}`,
                                        boxShadow: '0 0 0 2px ' + (event.date ? event.color : theme.palette.grey[400])
                                    }} />
                                    <Typography variant="caption" sx={{ mt: 1, fontWeight: 600, color: theme.palette.text.primary }}>
                                        {event.name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}>
                                        {event.date ? event.date.toLocaleDateString() : 'N/A'}
                                    </Typography>
                                </Stack>
                            </Tooltip>

                            {index < timelineEvents.length - 1 && (
                                <Box sx={{
                                    flexGrow: 1,
                                    position: 'relative',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: 40
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: theme.palette.background.paper,
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        border: `1px solid ${theme.palette.divider}`,
                                        boxShadow: theme.shadows[1],
                                        zIndex: 2,
                                        whiteSpace: 'nowrap',
                                        position: 'relative',
                                        '&:before, &:after': {
                                            content: '""',
                                            position: 'absolute',
                                            top: '50%',
                                            width: '50%',
                                            height: '1px',
                                            backgroundColor: theme.palette.divider
                                        },
                                        '&:before': {
                                            right: '100%',
                                            marginRight: '8px'
                                        },
                                        '&:after': {
                                            left: '100%',
                                            marginLeft: '8px'
                                        }
                                    }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                fontSize: '0.7rem',
                                                fontWeight: 500,
                                                color: theme.palette.text.primary,
                                                whiteSpace: 'nowrap',
                                                display: 'inline-block',
                                                px: 0.5
                                            }}
                                        >
                                            {daysToNext !== null ? `${daysToNext} day${daysToNext !== 1 ? 's' : ''}` : ''}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );

    const dateFields = [
        { label: 'Applications Open', value: draftCampaign.applications_open, tooltip: 'Start date for accepting applications', field: 'applications_open' },
        { label: 'Applications Close', value: draftCampaign.applications_close, tooltip: 'Deadline for submitting applications', field: 'applications_close' },
        { label: 'Match Notification', value: draftCampaign.match_notification_date, tooltip: 'Date when matches are announced', field: 'match_notification_date' },
        { label: 'Matches Finalized', value: draftCampaign.matches_finalized, tooltip: 'Deadline for participants to confirm their match', field: 'matches_finalized' },
        { label: 'Kickoff', value: draftCampaign.kickoff, tooltip: 'Official start date of the mentorship program', field: 'kickoff' },
        { label: 'Closing', value: draftCampaign.closing, tooltip: 'Final date of the mentorship program', field: 'closing' },
        { label: 'Feedback Due', value: draftCampaign.feedback_deadline, tooltip: 'Deadline for submitting feedback', field: 'feedback_deadline' },
    ];

    // Handle inline change and persist if onSave provided
    const handleDateChange = async (field: keyof Campaign, newVal: Date | null) => {
        const formatted = newVal ? newVal.toISOString().split('T')[0] : null;
        const updated: Campaign = { ...draftCampaign, [field]: formatted } as Campaign;
        setDraftCampaign(updated);
        if (onSave) {
            try {
                await onSave({ [field]: formatted });
            } catch (e) {
                console.error('Error saving date', e);
                setSnackbar({ open: true, message: 'Failed to save date', severity: 'error' });
            }
        }
    };

    const handleOpenModal = () => {
        setKickoffDate(campaign.kickoff ? new Date(campaign.kickoff) : null);
        setClosingDate(campaign.closing ? new Date(campaign.closing) : null);
        setDateError('');
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const validateDates = (): boolean => {
        if (!kickoffDate || !closingDate) {
            setDateError('Both kickoff and closing dates are required');
            return false;
        }

        if (kickoffDate >= closingDate) {
            setDateError('Kickoff date must be before closing date');
            return false;
        }

        return true;
    };

    const handleGenerateDates = async () => {
        if (!validateDates()) {
            return;
        }

        if (!kickoffDate || !closingDate) {
            setSnackbar({
                open: true,
                message: 'Please select both kickoff and closing dates',
                severity: 'error'
            });
            return;
        }

        handleCloseModal();

        setLoading(true);
        setSnackbar({
            open: true,
            message: 'Generating dates with AI...',
            severity: 'info'
        });

        try {
            // Create AI service instance
            const aiService = new AIService();
            
            // Format dates for API (YYYY-MM-DD)
            const formatDate = (date: Date): string => {
                return date.toISOString().split('T')[0];
            };

            // Prepare request payload
            const dateRequest: DateRequest = {
                kickoff: formatDate(kickoffDate),
                closing: formatDate(closingDate)
            };

            // Call AI service to generate dates
            const generatedDates: CampaignDates = await aiService.generateDates(dateRequest);

            // Create updated campaign object with generated dates
            const updatedCampaign: Partial<Campaign> = {
                kickoff: generatedDates.kickoff,
                closing: generatedDates.closing,
                applications_open: generatedDates.applications_open,
                applications_close: generatedDates.applications_close,
                match_notification_date: generatedDates.match_notification_date,
                matches_finalized: generatedDates.matches_finalized,
                feedback_deadline: generatedDates.feedback_deadline
            };

            // Update campaign with generated dates
            if (onSave && organizationId && campaignId) {
                await onSave(updatedCampaign);
                setSnackbar({
                    open: true,
                    message: 'Campaign dates generated and saved successfully',
                    severity: 'success'
                });
            } else {
                // If onSave is not provided, just show success message
                setSnackbar({
                    open: true,
                    message: 'Campaign dates generated successfully',
                    severity: 'success'
                });
            }
        } catch (error) {
            console.error('Error generating dates:', error);
            setSnackbar({
                open: true,
                message: 'Failed to generate dates. Please try again.',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
                {renderTimeline()}
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Key Dates</Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesome />}
                            onClick={handleOpenModal}
                            disabled={loading}
                        >
                            Generate
                        </Button>
                    </Box>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {dateFields.map(field => (
                            <EditableDateField
                                key={field.label}
                                label={field.label}
                                value={field.value}
                                tooltip={field.tooltip}
                                fieldKey={field.field as keyof Campaign}
                                onDateChange={handleDateChange}
                            />

                        ))}
                    </Grid>
                </Paper>
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                {/* Date Selection Modal */}
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                        <DialogTitle>Generate Campaign Dates</DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" sx={{ mb: 3 }}>
                                Please select the kickoff and closing dates for your campaign.
                                The AI will generate all other dates based on these two key dates.
                            </Typography>

                            {dateError && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {dateError}
                                </Alert>
                            )}

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <DatePicker
                                        label="Kickoff Date"
                                        value={kickoffDate}
                                        onChange={(newValue) => setKickoffDate(newValue)}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                margin: 'normal',
                                                helperText: 'When the mentorship program starts'
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DatePicker
                                        label="Closing Date"
                                        value={closingDate}
                                        onChange={(newValue) => setClosingDate(newValue)}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                margin: 'normal',
                                                helperText: 'When the mentorship program ends'
                                            }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseModal}>Cancel</Button>
                            <Button
                                onClick={handleGenerateDates}
                                variant="contained"
                                color="primary"
                                disabled={loading}
                            >
                                {loading ? 'Generating...' : 'Generate Dates'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </LocalizationProvider>
            </Box>
        </LocalizationProvider>
    );
};

export default Dates;
